/**
 * Container detail — the administrator surface.
 *
 * Details, metadata model, retention, legal hold, access and the exit package.
 *
 * Editable for as long as the archive is open. A records archive is long-lived
 * and its configuration legitimately changes over years; what makes that
 * defensible is the audit trail, not a freeze. A *closed* archive accepts
 * neither new records nor configuration changes — that is what closed means.
 */

import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconDownload,
  IconLock,
  IconPlugConnected,
  IconSearch,
  IconUpload,
} from "@tabler/icons-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAccessToken } from "@/features/auth/useAccessToken";
import { useMe } from "@/features/users/useMe";
import {
  exportArchiveContainer,
  getArchiveContainer,
  setArchiveLegalHold,
  updateArchiveContainer,
  type ArchiveContainer,
  type ArchiveField,
} from "@/shared/api/archive";
import { ArchiveAccessTab } from "./ArchiveAccessTab";
import { ArchiveUploadModal } from "./ArchiveUploadModal";
import { ArchiveDetailsTab } from "./ArchiveDetailsTab";
import { clusterColour, clusterTitle, formatBytes, formatCount, formatDate } from "./format";

export function ArchiveContainerDetailPage() {
  const { containerId = "" } = useParams();
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const isAdmin = (me?.roles ?? []).includes("administrator");
  const [uploadOpen, { open: openUpload, close: closeUpload }] = useDisclosure(false);

  const [container, setContainer] = useState<ArchiveContainer | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      setContainer(await getArchiveContainer(token, containerId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [containerId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Alert color="orange" variant="light" title="No access to this archive">
        {error}
        <Text size="xs" c="dimmed" mt={6}>
          Access is granted per archival container by an administrator.
        </Text>
      </Alert>
    );
  }
  if (!container) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  const editable = isAdmin && container.lifecycle_state !== "archived";

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>{container.name}</Title>
          <Text size="sm" c="dimmed" mt={4}>
            {container.description}
          </Text>
        </div>
        <Group gap="xs">
          <Button
            variant="light"
            leftSection={<IconSearch size={16} />}
            onClick={() => navigate(`/archive/search?container=${container.container_id}`)}
          >
            Search this archive
          </Button>
          {isAdmin && container.kind === "documents" && (
            <Button variant="light" leftSection={<IconUpload size={16} />} onClick={openUpload}>
              Add documents
            </Button>
          )}
          {isAdmin && <ExportButton container={container} />}
        </Group>
      </Group>

      <Group gap="xs">
        <Badge color={clusterColour(container.cluster)} variant="light">
          {clusterTitle(container.cluster)}
        </Badge>
        <Badge variant="default" tt="none">
          {container.source_system}
        </Badge>
        <Badge variant="default" tt="none">
          {formatCount(container.record_count)} records
        </Badge>
        <Badge variant="default" tt="none">
          {formatBytes(container.bytes_total)}
        </Badge>
        <Badge variant="default" tt="none">
          {container.storage_class}
        </Badge>
        <Badge variant="default" tt="none" leftSection={<IconLock size={11} />}>
          {container.retention_class}
          {container.object_lock_mode ? ` · Object Lock ${container.object_lock_mode}` : ""}
        </Badge>
        <Badge variant="default" tt="none">
          {container.lifecycle_state}
        </Badge>
      </Group>

      {!container.storage_enforces_retention && container.object_lock_mode && (
        <Alert color="yellow" variant="light">
          Storage backend is <strong>{container.storage_backend}</strong> — retention is recorded on
          each record but not enforced by the storage layer. Point the archive at S3 with Object
          Lock for enforcement.
        </Alert>
      )}

      {isAdmin && (
        <ArchiveUploadModal
          opened={uploadOpen}
          onClose={closeUpload}
          container={container}
          onUploaded={load}
        />
      )}

      {/* `?? []` rather than trusting the payload. `fed_by` is optional on the
          wire and absent from anything served by a backend older than 0020 —
          and a detail page that white-screens over a missing optional array is
          a page that white-screens in front of someone. */}
      {(container.fed_by ?? []).length > 0 && (
        <Paper withBorder p="sm" radius="md">
          <Group gap="xs" mb={6}>
            <IconPlugConnected size={15} />
            <Text size="xs" fw={700} tt="uppercase" c="dimmed">
              Fed by
            </Text>
            <Text size="xs" c="dimmed">
              observed from the records themselves, not typed in
            </Text>
          </Group>
          <Group gap="sm">
            {(container.fed_by ?? []).map((source, i) => (
              <Paper key={i} withBorder p="xs" radius="sm" bg="var(--mantine-color-body)">
                <Group gap={8} wrap="nowrap">
                  <div>
                    <Text size="sm" fw={500}>
                      {source.connection_name || "Unattributed"}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatCount(source.record_count)} records
                      {source.last_ingest_utc
                        ? ` · last ${formatDate(source.last_ingest_utc)}`
                        : ""}
                    </Text>
                  </div>
                  {/* A live connection is a link you can still follow. A gone
                      one is the normal end state for a retired system, and the
                      badge says so rather than showing a broken link. */}
                  <Badge
                    size="xs"
                    variant="light"
                    color={source.connection_id ? "teal" : "gray"}
                    tt="none"
                  >
                    {source.connection_id ? source.source_type || "connected" : "source retired"}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Group>
        </Paper>
      )}

      <Tabs defaultValue="details">
        <Tabs.List mb="md">
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="metadata">Metadata model</Tabs.Tab>
          <Tabs.Tab value="retention">Retention</Tabs.Tab>
          <Tabs.Tab value="access">Access &amp; rules</Tabs.Tab>
          <Tabs.Tab value="storage">Storage</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details">
          <ArchiveDetailsTab container={container} canEdit={isAdmin} onSaved={load} />
        </Tabs.Panel>
        <Tabs.Panel value="metadata">
          <MetadataTab container={container} editable={editable} onSaved={load} />
        </Tabs.Panel>
        <Tabs.Panel value="retention">
          <RetentionTab
            container={container}
            isAdmin={isAdmin}
            editable={editable}
            onSaved={load}
          />
        </Tabs.Panel>
        <Tabs.Panel value="access">
          <ArchiveAccessTab containerId={container.container_id} canManage={isAdmin} />
        </Tabs.Panel>
        <Tabs.Panel value="storage">
          <Paper withBorder p="md" radius="md" maw={620}>
            <Table verticalSpacing={6}>
              <Table.Tbody>
                {[
                  ["Backend", container.storage_backend],
                  ["Prefix", container.storage_prefix],
                  ["Storage class", container.storage_class],
                  ["Object Lock", container.object_lock_mode ?? "not applied"],
                  [
                    "Retention enforcement",
                    container.storage_enforces_retention
                      ? "enforced by the storage layer"
                      : "recorded only",
                  ],
                  ["Created", formatDate(container.created_at_utc)],
                ].map(([label, value]) => (
                  <Table.Tr key={label}>
                    <Table.Td w={200}>
                      <Text size="xs" c="dimmed">
                        {label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{value}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function MetadataTab({
  container,
  editable,
  onSaved,
}: {
  container: ArchiveContainer;
  editable: boolean;
  onSaved: () => void;
}) {
  const getToken = useAccessToken();
  const [fields, setFields] = useState<ArchiveField[]>(container.fields ?? []);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => setFields(container.fields ?? []), [container]);

  async function save() {
    setBusy(true);
    try {
      const token = await getToken();
      await updateArchiveContainer(token, container.container_id, { fields });
      notifications.show({
        title: "Metadata model updated",
        message: "New facetable fields become search filters immediately.",
        color: "teal",
      });
      setDirty(false);
      onSaved();
    } catch (err) {
      notifications.show({
        title: "Update failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md" maw={880}>
      <Text size="sm" c="dimmed">
        The metadata model drives ingest mapping, search facets and this screen — one definition,
        three consumers. Fields classified <strong>pii</strong> or <strong>phi</strong> are never
        offered as search facets.
      </Text>

      {!editable && (
        <Alert color="blue" variant="light">
          {container.lifecycle_state === "archived"
            ? "This archive is closed. It accepts no new records and no configuration changes."
            : "Only an administrator can change the metadata model."}
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Key</Table.Th>
              <Table.Th>Label</Table.Th>
              <Table.Th w={110}>Type</Table.Th>
              <Table.Th w={150}>Classification</Table.Th>
              <Table.Th w={100}>Facetable</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fields.map((field, index) => (
              <Table.Tr key={field.key}>
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {field.key}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{field.label}</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="default" tt="none">
                    {field.type}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {editable ? (
                    <Select
                      size="xs"
                      value={field.classification}
                      data={["public", "confidential", "pii", "phi"]}
                      onChange={(value) => {
                        setFields((prev) =>
                          prev.map((f, i) =>
                            i === index ? { ...f, classification: value ?? "confidential" } : f,
                          ),
                        );
                        setDirty(true);
                      }}
                    />
                  ) : (
                    <Badge
                      size="sm"
                      variant="light"
                      tt="none"
                      color={
                        field.classification === "phi" || field.classification === "pii"
                          ? "orange"
                          : "gray"
                      }
                    >
                      {field.classification}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>
                  <Switch
                    size="xs"
                    checked={field.facetable}
                    disabled={!editable}
                    onChange={(e) => {
                      setFields((prev) =>
                        prev.map((f, i) =>
                          i === index ? { ...f, facetable: e.currentTarget.checked } : f,
                        ),
                      );
                      setDirty(true);
                    }}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {editable && (
        <Button size="xs" w="fit-content" disabled={!dirty} loading={busy} onClick={save}>
          Save metadata model
        </Button>
      )}
    </Stack>
  );
}

function RetentionTab({
  container,
  isAdmin,
  editable,
  onSaved,
}: {
  container: ArchiveContainer;
  isAdmin: boolean;
  editable: boolean;
  onSaved: () => void;
}) {
  const getToken = useAccessToken();
  const [retention, setRetention] = useState(container.retention_class);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function saveRetention() {
    setBusy(true);
    try {
      const token = await getToken();
      await updateArchiveContainer(token, container.container_id, {
        retention_class: retention,
      });
      notifications.show({
        title: "Retention updated",
        message: "Applies to the next intake. Records already archived keep their term.",
        color: "teal",
      });
      onSaved();
    } catch (err) {
      notifications.show({
        title: "Update failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  async function toggleHold() {
    setBusy(true);
    try {
      const token = await getToken();
      const result = await setArchiveLegalHold(
        token,
        container.container_id,
        !container.default_legal_hold,
        reason || "not stated",
      );
      notifications.show({
        title: "Legal hold updated",
        message: `${formatCount(result.objects_affected)} records affected.`,
        color: "teal",
      });
      onSaved();
    } catch (err) {
      notifications.show({
        title: "Update failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="md" maw={640}>
      <Paper withBorder p="md" radius="md">
        <Text fw={600} mb="xs">
          Retention policy
        </Text>
        <Group align="flex-end" gap="sm">
          <Select
            label="Retention class"
            w={170}
            value={retention}
            disabled={!editable}
            data={["5y", "10y", "15y", "30y", "permanent"]}
            onChange={(value) => setRetention(value ?? "10y")}
          />
          {editable && (
            <Button
              size="sm"
              disabled={retention === container.retention_class}
              loading={busy}
              onClick={saveRetention}
            >
              Save
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          Retain-until is derived from each record&apos;s own effective date, not its ingest date.
          Changing the class affects the next intake — records already archived keep the term they
          were archived under.
        </Text>
      </Paper>

      <Paper withBorder p="md" radius="md">
        <Text fw={600} mb="xs">
          Legal hold
        </Text>
        <Text size="sm" c="dimmed" mb="sm">
          {container.default_legal_hold
            ? "A hold is applied. Held records cannot be disposed of, whatever their retention date."
            : "No legal hold applied."}
        </Text>
        {isAdmin && (
          <Group align="flex-end">
            <TextInput
              label="Reason"
              description="Recorded in the audit trail — a hold without one is not defensible"
              flex={1}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              placeholder="e.g. litigation hold — matter 2026-114"
            />
            <Button
              color={container.default_legal_hold ? "gray" : "orange"}
              loading={busy}
              onClick={toggleHold}
            >
              {container.default_legal_hold ? "Release hold" : "Apply hold"}
            </Button>
          </Group>
        )}
      </Paper>
    </Stack>
  );
}

function ExportButton({ container }: { container: ArchiveContainer }) {
  const getToken = useAccessToken();
  const [opened, { open, close }] = useDisclosure(false);
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      const token = await getToken();
      await exportArchiveContainer(token, container.container_id, `${container.code}-export.zip`);
      notifications.show({
        title: "Exit package generated",
        message: "Open formats, full manifest, checksums and an audit extract.",
        color: "teal",
      });
      close();
    } catch (err) {
      notifications.show({
        title: "Export failed",
        message: err instanceof Error ? err.message : String(err),
        color: "red",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant="light" leftSection={<IconDownload size={16} />} onClick={open}>
        Export / exit
      </Button>
      <Modal opened={opened} onClose={close} title="Data portability & exit package" size="lg">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            An exit package in open, documented formats. No DAQUMA software is required to read it.
          </Text>
          <Paper withBorder p="sm" radius="md">
            <Stack gap={4}>
              {[
                "manifest.json — what the package contains and how it was produced",
                "objects/ — archived documents, byte-identical to the originals",
                "datasets/ — archived structured extracts as CSV",
                "metadata.csv — the metadata model and values for every object",
                "audit-extract.jsonl — hash-chained audit events for this container",
                "checksums.sha256 — verify with sha256sum -c",
              ].map((line) => (
                <Text key={line} size="xs" ff="monospace">
                  {line}
                </Text>
              ))}
            </Stack>
          </Paper>
          <Text size="xs" c="yellow">
            Bounded export — the manifest declares whether the result is a subset of the container.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>
              Cancel
            </Button>
            <Button loading={busy} onClick={run}>
              Generate package
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
